import Vue from 'vue'
import VueI18n from 'vue-i18n'

import { kebabCase } from 'lodash'

import messages from './locales/messages/en-GB.json'
import datetimeFormats from './locales/datetime-formats/en-GB.json'
import numberFormats from './locales/number-formats/en-GB.json'
import supportedLocales from './locales/supportedLocales'

const i18n = setup(Vue)

export function setup (vueInstance) {
  vueInstance.use(VueI18n)

  return new VueI18n({
    locale: 'en-GB',
    messages: { 'en-GB': messages },
    dateTimeFormats: { 'en-GB': datetimeFormats },
    numberFormats: { 'en-GB': numberFormats }
  })
}

export const loadLocales = {
  loadedLocales: {
    messages: ['en-GB'],
    datetimeFormats: ['en-GB'],
    numberFormats: ['en-GB']
  },

  install: function (Vue) {
    // this.setLocale(this.getUserLocale()) // TODO: Enable language per user
  },

  /**
   * Load messages asynchronous for the given locale
   *
   * @param {String} locale
   * @return {Promise<Object>}
   */
  loadMessagesAsync: function (locale) {
    return this.loadResourcesAsync(locale, 'messages').then(messages => {
      if (messages) {
        i18n.setLocaleMessage(locale, messages.default)
      }
    })
  },

  /**
   * Load datetime formats asynchronous for the given locale
   *
   * @param {String} locale
   * @return {Promise<Object>}
   */
  loadDatetimeFormatsAsync: function (locale) {
    return this.loadResourcesAsync(locale, 'datetimeFormats').then(datetimeFormats => {
      if (datetimeFormats) {
        i18n.setDateTimeFormat(locale, datetimeFormats.default)
      }
    })
  },

  /**
   * Load number formats asynchronous for the given locale
   *
   * @param {String} locale
   * @return {Promise<Object>}
   */
  loadNumberFormatsAsync: function (locale) {
    return this.loadResourcesAsync(locale, 'numberFormats').then(numberFormats => {
      if (numberFormats) {
        i18n.setNumberFormat(locale, numberFormats.default)
      }
    })
  },

  /**
   * Load messages asynchronous for the given locale
   *
   * @param {String} locale
   * @param {String} resourceType
   * @return {Promise<Object>}
   */
  loadResourcesAsync: function (locale, resourceType) {
    if (!this.loadedLocales[resourceType].includes(locale)) {
      const path = kebabCase(resourceType)

      return import(/* webpackChunkName: "lang-[request]" */ `@/locales/${path}/${locale}`).then(resources => {
        this.loadedLocales[resourceType].push(locale)
        return resources
      })
    }
    return Promise.resolve()
  },

  /**
   * Get the user locale
   */
  getUserLocale: function () {
    return window.navigator.language || window.navigator.userLanguage || 'en'
  },

  /**
   * Set the preferred locale
   *
   * @param {String} locale
   */
  setLocale: function (locale) {
    if (!this.isLocaleSupported(locale)) {
      // Locale is not supported
      // If the locale is an ISO locale, try only the language
      const splittedLocale = locale.split('-')
      if (splittedLocale.length > 1) {
        return this.setLocale(splittedLocale[0])
      }

      return
    }

    const loadMessagesPromise = this.loadMessagesAsync(locale)
    const loadDatetimeFormatsPromise = this.loadDatetimeFormatsAsync(locale)
    const loadNumberFormatsPromise = this.loadNumberFormatsAsync(locale)

    Promise.all([ loadMessagesPromise, loadDatetimeFormatsPromise, loadNumberFormatsPromise ]).then(() => {
      i18n.locale = locale
      document.querySelector('html').setAttribute('lang', locale)
    })
  },

  /**
   * Is the given locale supported?
   *
   * @param {String} locale
   */
  isLocaleSupported: function (locale) {
    return supportedLocales.indexOf(locale) >= 0
  }
}

export default i18n
